import React, { useState, useMemo } from "react";
import { useDistrict } from "@/context/DistrictContext";
import { SectionHeader, Reveal, FlatCard } from "@/components/primitives";
import { motion } from "framer-motion";
import { Compass, MapPin, Eye, EyeOff } from "lucide-react";
import HeritageSiteModal from "@/components/HeritageSiteModal";

const LAYERS = [
    { id: "boundaries", label: "Block Boundaries", default: true },
    { id: "rivers", label: "River Systems", default: true },
    { id: "religious", label: "Religious", default: true },
    { id: "buddhist", label: "Buddhist", default: true },
    { id: "forts", label: "Forts & Palaces", default: true },
    { id: "institutions", label: "Institutions", default: true },
    { id: "craft", label: "Craft Clusters", default: true },
];

const typeMap = {
    "Religious": "religious",
    "Buddhist": "buddhist",
    "Forts": "forts",
    "Heritage Site": "forts",
    "Institutions": "institutions",
    "Craft": "craft",
};

const findFullSite = (district, name) =>
    district?.heritage_sites?.find((s) => s.name === name || name?.startsWith(s.name) || s.name?.startsWith(name));

const HeritageMap = () => {
    const { district, loading } = useDistrict();
    const [layers, setLayers] = useState(() =>
        Object.fromEntries(LAYERS.map((l) => [l.id, l.default]))
    );
    const [selected, setSelected] = useState(null);

    const toggle = (id) => setLayers((l) => ({ ...l, [id]: !l[id] }));

    const visiblePoints = useMemo(() => {
        if (!district) return [];
        return district.map.heritage_points.filter((p) => {
            const k = typeMap[p.type] || "religious";
            return layers[k];
        });
    }, [district, layers]);

    if (loading || !district) {
        return <div className="min-h-[60vh] grid place-items-center font-display text-2xl italic ink-mute">Loading map…</div>;
    }

    const accent = district.accent;

    const openPin = (pin) => {
        const full = findFullSite(district, pin.name);
        setSelected(full || pin);
    };

    return (
        <div className="bg-ivory">
            <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-16">
                <SectionHeader
                    kicker={`${district.name} · Heritage Cartography`}
                    title="A Map Drawn From Memory"
                    lede="Rivers, blocks, and the architecture of belonging. Tap any pin to open a full account of the site — its commissioner, materials, opening hours, and a one-tap link to Google Maps."
                />

                <div className="grid lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-9 relative">
                        <div className="relative bg-[#FBF8F2] hairline border rounded-md overflow-hidden" data-testid="heritage-map-container">
                            <svg viewBox="0 0 920 600" className="w-full h-auto" role="img" aria-label={`Heritage map of ${district.name}`}>
                                <defs>
                                    <pattern id="paper" patternUnits="userSpaceOnUse" width="6" height="6">
                                        <rect width="6" height="6" fill="#FBF8F2" />
                                        <circle cx="1" cy="1" r="0.4" fill="#E8DFC8" />
                                    </pattern>
                                    <linearGradient id="river-grad" x1="0" x2="1">
                                        <stop offset="0" stopColor="#5C7E9B" stopOpacity="0.85" />
                                        <stop offset="1" stopColor="#7DA3C4" stopOpacity="0.6" />
                                    </linearGradient>
                                </defs>
                                <rect width="920" height="600" fill="url(#paper)" />

                                {layers.boundaries && district.map.blocks.map((b) => (
                                    <g key={b.name}>
                                        <path d={b.path} fill="none" stroke="#121A2F" strokeOpacity="0.45" strokeWidth="1" strokeDasharray="3,4" />
                                        <text x={b.label_x} y={b.label_y} fill="#121A2F" fontSize="11" fontFamily="Chivo" letterSpacing="2" textAnchor="middle">
                                            {b.name.toUpperCase()}
                                        </text>
                                    </g>
                                ))}

                                {layers.rivers && district.map.rivers.map((r) => (
                                    <g key={r.name}>
                                        <path d={r.path} stroke="url(#river-grad)" strokeWidth="6" fill="none" strokeLinecap="round" opacity="0.9" />
                                        <path d={r.path} stroke="#5C7E9B" strokeWidth="1" fill="none" strokeDasharray="2,3" opacity="0.55" />
                                    </g>
                                ))}

                                {visiblePoints.map((p, i) => (
                                    <g
                                        key={p.id}
                                        data-testid={`map-pin-${p.id}`}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => openPin(p)}
                                    >
                                        <motion.circle
                                            cx={p.x} cy={p.y} r="14"
                                            fill={accent} fillOpacity="0.18"
                                            animate={{ scale: [1, 1.25, 1] }}
                                            transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.15 }}
                                        />
                                        <circle cx={p.x} cy={p.y} r="6" fill={accent} stroke="#fff" strokeWidth="2" />
                                        <text x={p.x + 12} y={p.y + 4} fontSize="11" fontFamily="Chivo" fill="#121A2F">
                                            {p.name}
                                        </text>
                                    </g>
                                ))}

                                <g transform="translate(860, 50)" data-testid="north-arrow">
                                    <circle cx="0" cy="0" r="22" fill="#fff" stroke="#121A2F" strokeWidth="1" />
                                    <polygon points="0,-16 5,4 0,0 -5,4" fill="#C05640" />
                                    <text y="34" textAnchor="middle" fontSize="10" fontFamily="Chivo" letterSpacing="2" fill="#121A2F">N</text>
                                </g>

                                <g transform="translate(40, 560)" data-testid="scale-bar">
                                    <rect x="0" y="0" width="60" height="6" fill="#121A2F" />
                                    <rect x="60" y="0" width="60" height="6" fill="#fff" stroke="#121A2F" strokeWidth="1" />
                                    <text x="0" y="22" fontSize="10" fontFamily="Chivo" fill="#121A2F">0</text>
                                    <text x="120" y="22" fontSize="10" fontFamily="Chivo" fill="#121A2F">10 km</text>
                                </g>
                            </svg>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-4 font-data text-[11px] ink-mute" data-testid="map-legend">
                            <span className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full" style={{ background: accent }} /> Heritage Pin
                            </span>
                            <span className="flex items-center gap-2">
                                <span className="w-3 h-1" style={{ background: "#5C7E9B" }} /> River
                            </span>
                            <span className="flex items-center gap-2">
                                <span className="w-3 h-px border-t border-dashed border-[#121A2F]" /> Block boundary
                            </span>
                        </div>
                    </div>

                    <aside className="lg:col-span-3 space-y-5">
                        <FlatCard className="p-5">
                            <div className="font-data text-[10px] tracking-[0.22em] uppercase ink-mute mb-3 flex items-center gap-2"><Compass className="w-3.5 h-3.5" /> Layers</div>
                            <div className="space-y-2">
                                {LAYERS.map((l) => (
                                    <button
                                        key={l.id}
                                        data-testid={`layer-toggle-${l.id}`}
                                        onClick={() => toggle(l.id)}
                                        className="flex items-center justify-between w-full text-left font-data text-xs py-1.5 hover:terra"
                                    >
                                        <span>{l.label}</span>
                                        {layers[l.id] ? <Eye className="w-3.5 h-3.5 terra" /> : <EyeOff className="w-3.5 h-3.5 ink-mute" />}
                                    </button>
                                ))}
                            </div>
                        </FlatCard>

                        <FlatCard className="p-5">
                            <div className="font-data text-[10px] tracking-[0.22em] uppercase ink-mute mb-3 flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> All Sites</div>
                            <div className="space-y-3 max-h-[440px] overflow-y-auto pr-1" data-testid="all-sites-list">
                                {district.heritage_sites.map((s, i) => (
                                    <button
                                        key={s.name}
                                        data-testid={`site-link-${i}`}
                                        onClick={() => setSelected(s)}
                                        className="block w-full text-left py-2 hairline-b last:border-b-0 group"
                                    >
                                        <div className="font-display text-base leading-tight group-hover:terra transition-colors">{s.name}</div>
                                        <div className="font-data text-[10px] uppercase tracking-[0.18em] ink-mute mt-0.5">{s.type} · {s.condition}</div>
                                    </button>
                                ))}
                            </div>
                        </FlatCard>
                    </aside>
                </div>
            </div>

            <HeritageSiteModal
                site={selected}
                open={!!selected}
                onClose={() => setSelected(null)}
                accent={accent}
                districtName={district.name}
            />
        </div>
    );
};

export default HeritageMap;
